import React, { useState, useEffect } from 'react';
import {
  FiStar, FiDownload, FiAlertCircle, FiRefreshCw, FiFilter, FiUser, FiMessageSquare, FiBriefcase, FiCheckCircle
} from 'react-icons/fi';
import {
  unifiedReviewApi, UnifiedReview, UnifiedReviewStatistics, ReviewType
} from '../services/supportApi';
import './UnifiedSurveyManagement.css';

const Icon = ({ icon: IconComponent, className }: { icon: any; className?: string }) =>
  React.createElement(IconComponent as any, { className });

const UnifiedSurveyManagement: React.FC = () => {
  const [reviews, setReviews] = useState<UnifiedReview[]>([]);
  const [statistics, setStatistics] = useState<UnifiedReviewStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [reviewTypeFilter, setReviewTypeFilter] = useState<ReviewType | 'all'>('all');
  const [lowRatingOnly, setLowRatingOnly] = useState(false);

  useEffect(() => {
    loadData();
  }, [page, reviewTypeFilter, lowRatingOnly]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load statistics
      const stats = await unifiedReviewApi.getStatistics();
      setStatistics(stats);

      // Load reviews
      const reviewType = reviewTypeFilter === 'all' ? undefined : reviewTypeFilter;
      const response = await unifiedReviewApi.getAll(page, size, lowRatingOnly, reviewType);
      setReviews(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      console.error('Failed to load reviews:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const reviewType = reviewTypeFilter === 'all' ? undefined : reviewTypeFilter;
      const blob = await unifiedReviewApi.exportExcel(lowRatingOnly, reviewType);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `unified_reviews_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`Review data exported successfully!`);
    } catch (error: any) {
      console.error('Failed to export reviews:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating <= 2) return '#f44336';
    if (rating === 3) return '#ff9800';
    if (rating === 4) return '#2196f3';
    return '#4caf50';
  };

  const getRatingStars = (rating: number) => {
    return '⭐'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating));
  };

  const getReviewTypeIcon = (type: ReviewType) => {
    switch (type) {
      case 'TICKET_SURVEY':
        return FiMessageSquare;
      case 'CHAT_SURVEY':
        return FiUser;
      case 'USER_REVIEW':
        return FiUser;
      case 'PROJECT_REVIEW':
        return FiBriefcase;
      case 'SMALL_TASK_REVIEW':
        return FiCheckCircle;
      default:
        return FiStar;
    }
  };

  const getReviewTypeColor = (type: ReviewType) => {
    switch (type) {
      case 'TICKET_SURVEY':
        return '#2196f3';
      case 'CHAT_SURVEY':
        return '#0080E0';
      case 'USER_REVIEW':
        return '#4caf50';
      case 'PROJECT_REVIEW':
        return '#ff9800';
      case 'SMALL_TASK_REVIEW':
        return '#00bcd4';
      default:
        return '#666';
    }
  };

  const renderReviewInfo = (review: UnifiedReview) => {
    const infoRows: React.ReactElement[] = [];

    // Common fields
    infoRows.push(
      <div key="reviewer" className="info-row">
        <strong>Reviewer:</strong> {review.reviewerName}
        {review.reviewerPhone && ` (${review.reviewerPhone})`}
      </div>
    );

    // Type-specific fields
    switch (review.reviewType) {
      case 'USER_REVIEW':
        if (review.reviewedUserName) {
          infoRows.push(
            <div key="reviewed" className="info-row">
              <strong>Reviewed User:</strong> {review.reviewedUserName}
            </div>
          );
        }
        if (review.projectId) {
          infoRows.push(
            <div key="project" className="info-row">
              <strong>Project ID:</strong> #{review.projectId}
            </div>
          );
        }
        break;
      case 'PROJECT_REVIEW':
        if (review.reviewedUserName) {
          infoRows.push(
            <div key="reviewed" className="info-row">
              <strong>Reviewed User:</strong> {review.reviewedUserName}
            </div>
          );
        }
        if (review.projectId) {
          infoRows.push(
            <div key="project" className="info-row">
              <strong>Project ID:</strong> #{review.projectId}
            </div>
          );
        }
        break;
      case 'SMALL_TASK_REVIEW':
        if (review.reviewedUserName) {
          infoRows.push(
            <div key="reviewed" className="info-row">
              <strong>Reviewed User:</strong> {review.reviewedUserName}
            </div>
          );
        }
        if (review.smallTaskRequestId) {
          infoRows.push(
            <div key="small-task" className="info-row">
              <strong>Small Task ID:</strong> #{review.smallTaskRequestId}
            </div>
          );
        }
        break;
      case 'TICKET_SURVEY':
        if (review.supportTicketId) {
          infoRows.push(
            <div key="ticket" className="info-row">
              <strong>Ticket:</strong> #{review.supportTicketId}
              {review.ticketSubject && ` - ${review.ticketSubject}`}
            </div>
          );
        }
        if (review.supportAgentName) {
          infoRows.push(
            <div key="agent" className="info-row">
              <strong>Support Agent:</strong> {review.supportAgentName}
            </div>
          );
        }
        break;
      case 'CHAT_SURVEY':
        if (review.supportChatRequestId) {
          infoRows.push(
            <div key="chat" className="info-row">
              <strong>Chat Request:</strong> #{review.supportChatRequestId}
              {review.chatSubject && ` - ${review.chatSubject}`}
            </div>
          );
        }
        if (review.supportAgentName) {
          infoRows.push(
            <div key="agent" className="info-row">
              <strong>Support Agent:</strong> {review.supportAgentName}
            </div>
          );
        }
        break;
    }

    // Category Ratings (Sub-reviews)
    if (review.categoryRatings && review.categoryRatings.length > 0) {
      infoRows.push(
        <div key="category-ratings" className="category-ratings">
          <label>Category Ratings:</label>
          <div className="ratings-grid">
            {review.categoryRatings.map((cr) => (
              <div key={cr.categoryId} className="category-rating-item">
                <span className="cat-name">{cr.categoryNameEn}</span>
                <span className="cat-stars" style={{ color: getRatingColor(cr.ratingValue) }}>
                  {'★'.repeat(cr.ratingValue)}{'☆'.repeat(5 - cr.ratingValue)}
                  <span className="cat-rating-num">({cr.ratingValue})</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Comment
    if (review.comment) {
      infoRows.push(
        <div key="comment" className="info-row comment">
          <strong>Comment:</strong> {review.comment}
        </div>
      );
    }

    // Timestamps
    infoRows.push(
      <div key="created" className="info-row timestamps">
        <strong>Created:</strong> {new Date(review.createdAt).toLocaleString()}
      </div>
    );

    if (review.lowRatingAlertSentAt) {
      infoRows.push(
        <div key="alert" className="info-row alert-info">
          <strong>Alert Sent:</strong> {new Date(review.lowRatingAlertSentAt).toLocaleString()}
        </div>
      );
    }

    return infoRows;
  };

  return (
    <div className="unified-survey-management">
      <div className="usm-header">
        <h2>
          <Icon icon={FiStar} /> Unified Reviews & Surveys
        </h2>
        <div className="usm-actions">
          <button
            className="btn-secondary"
            onClick={loadData}
            disabled={loading}
          >
            <Icon icon={FiRefreshCw} /> Refresh
          </button>
          <button
            className="btn-primary"
            onClick={handleExportExcel}
            disabled={exporting}
          >
            <Icon icon={FiDownload} /> Export {lowRatingOnly ? 'Low Ratings' : 'All'}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="usm-statistics">
          <div className="stat-card">
            <div className="stat-label">Total Reviews</div>
            <div className="stat-value">{statistics.totalReviews}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Average Rating</div>
            <div className="stat-value rating-value" style={{ color: getRatingColor(Math.round(statistics.overallAverageRating)) }}>
              {statistics.overallAverageRating.toFixed(2)} ⭐
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Low Ratings (≤3)</div>
            <div className="stat-value warning">{statistics.totalLowRatings}</div>
          </div>
          {statistics.byType && Object.entries(statistics.byType).map(([type, stats]) => (
            <div key={type} className="stat-card" style={{ borderLeftColor: getReviewTypeColor(type as ReviewType) }}>
              <div className="stat-label">{type.replace(/_/g, ' ')}</div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-subvalue">Avg: {stats.averageRating.toFixed(1)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="usm-filters">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${!lowRatingOnly ? 'active' : ''}`}
            onClick={() => { setLowRatingOnly(false); setPage(0); }}
          >
            <Icon icon={FiFilter} /> All Reviews
          </button>
          <button
            className={`filter-btn ${lowRatingOnly ? 'active' : ''}`}
            onClick={() => { setLowRatingOnly(true); setPage(0); }}
          >
            <Icon icon={FiAlertCircle} /> Low Ratings (≤3)
          </button>
        </div>
        <div className="review-type-filter">
          <select
            value={reviewTypeFilter}
            onChange={(e) => { setReviewTypeFilter(e.target.value as ReviewType | 'all'); setPage(0); }}
          >
            <option value="all">All Review Types</option>
            <option value="TICKET_SURVEY">Ticket Surveys</option>
            <option value="CHAT_SURVEY">Chat Surveys</option>
            <option value="USER_REVIEW">User Reviews</option>
            <option value="PROJECT_REVIEW">Project Reviews</option>
            <option value="SMALL_TASK_REVIEW">Small Task Reviews</option>
          </select>
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="usm-loading">
          <div className="spinner"></div>
          <p>Loading reviews...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="usm-empty">
          <Icon icon={FiStar} />
          <p>No reviews found</p>
        </div>
      ) : (
        <>
          <div className="usm-surveys-list">
            {reviews.map((review) => (
              <div key={review.id} className={`usm-survey-card ${review.isLowRating ? 'low-rating' : ''}`}>
                <div className="usm-survey-header">
                  <div className="usm-survey-rating">
                    <span className="rating-stars" style={{ color: getRatingColor(review.rating) }}>
                      {getRatingStars(review.rating)}
                    </span>
                    <span className="rating-number">({review.rating.toFixed(1)}/5)</span>
                  </div>
                  <div className="usm-survey-badges">
                    <span
                      className="review-type-badge"
                      style={{
                        backgroundColor: getReviewTypeColor(review.reviewType) + '20',
                        color: getReviewTypeColor(review.reviewType),
                        borderColor: getReviewTypeColor(review.reviewType)
                      }}
                    >
                      <Icon icon={getReviewTypeIcon(review.reviewType)} /> {review.reviewTypeDisplay}
                    </span>
                    {review.isLowRating && (
                      <span className="low-rating-badge">
                        <Icon icon={FiAlertCircle} /> Low Rating
                      </span>
                    )}
                    {review.lowRatingAlertSent && (
                      <span className="alert-sent-badge">
                        <Icon icon={FiCheckCircle} /> Alert Sent
                      </span>
                    )}
                  </div>
                </div>
                <div className="usm-survey-content">
                  <div className="usm-survey-info">
                    {renderReviewInfo(review)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="usm-pagination">
              <button
                className="btn-pagination"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page + 1} of {totalPages} ({totalElements} total)
              </span>
              <button
                className="btn-pagination"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UnifiedSurveyManagement;

